// src/pages/Profile/hooks/usePushNotificationsPanel.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  disableAllPushNotifications,
  getExistingPushSubscription,
  getNotificationPermission,
  getPushPlatform,
  getPushStatus,
  isPushSupported,
  isStandalonePwa,
  sendTestPushNotification,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
} from "../../../services/pushNotificationService";

export default function usePushNotificationsPanel({
  user,
  onUserUpdated,
} = {}) {
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [currentDeviceSubscribed, setCurrentDeviceSubscribed] = useState(false);

  const [status, setStatus] = useState({
    push_notifications_enabled: !!user?.push_notifications_enabled,
    active_subscriptions_count: 0,
    has_active_subscription: false,
  });

  const supported = useMemo(() => isPushSupported(), []);
  const platform = useMemo(() => getPushPlatform(), []);
  const standalonePwa = useMemo(() => isStandalonePwa(), []);

  const permission = supported ? getNotificationPermission() : "unsupported";

  const isIOSNotStandalone = platform === "ios" && !standalonePwa;

  const refreshPushStatus = useCallback(async () => {
    if (!supported || !user?.id) return;

    try {
      setLoadingStatus(true);

      const existingSubscription = await getExistingPushSubscription();
      setCurrentDeviceSubscribed(!!existingSubscription);

      const data = await getPushStatus();

      setStatus({
        push_notifications_enabled: !!data.push_notifications_enabled,
        active_subscriptions_count: Number(
          data.active_subscriptions_count || 0,
        ),
        has_active_subscription: !!data.has_active_subscription,
      });

      onUserUpdated?.({
        push_notifications_enabled: !!data.push_notifications_enabled,
      });
    } catch (error) {
      console.error("Erreur refreshPushStatus:", error);
    } finally {
      setLoadingStatus(false);
    }
  }, [supported, user?.id, onUserUpdated]);

  useEffect(() => {
    refreshPushStatus();
  }, [refreshPushStatus]);

  const enablePush = async () => {
    if (!supported) {
      toast.error(
        "Les notifications push ne sont pas supportées sur cet appareil.",
      );
      return;
    }

    if (isIOSNotStandalone) {
      toast.info(
        "Sur iPhone, ajoutez d’abord le site à l’écran d’accueil, puis ouvrez l’application installée.",
      );
      return;
    }

    try {
      setSaving(true);

      const result = await subscribeToPushNotifications();

      if (!result?.success) {
        toast.error(
          result?.message || "Impossible d’activer les notifications push.",
        );
        return;
      }

      setStatus((previous) => ({
        ...previous,
        push_notifications_enabled: true,
        has_active_subscription: true,
        active_subscriptions_count:
          Number(previous.active_subscriptions_count || 0) || 1,
      }));

      setCurrentDeviceSubscribed(true);

      onUserUpdated?.({
        push_notifications_enabled: true,
      });

      toast.success("Notifications push activées sur cet appareil !");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’activation des notifications push.");
    } finally {
      setSaving(false);
      refreshPushStatus();
    }
  };

  const disableCurrentDevice = async () => {
    try {
      setSaving(true);

      const result = await unsubscribeFromPushNotifications();

      if (!result?.success) {
        toast.error(
          result?.message || "Impossible de désactiver cet appareil.",
        );
        return;
      }

      setCurrentDeviceSubscribed(false);

      setStatus((previous) => ({
        ...previous,
        push_notifications_enabled: !!result.push_notifications_enabled,
        active_subscriptions_count: Number(
          result.active_subscriptions_count || 0,
        ),
        has_active_subscription:
          Number(result.active_subscriptions_count || 0) > 0,
      }));

      onUserUpdated?.({
        push_notifications_enabled: !!result.push_notifications_enabled,
      });

      toast.success("Notifications push désactivées sur cet appareil.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la désactivation sur cet appareil.");
    } finally {
      setSaving(false);
      refreshPushStatus();
    }
  };

  const disableEverywhere = async () => {
    try {
      setSaving(true);

      const result = await disableAllPushNotifications();

      if (!result?.success) {
        toast.error(
          result?.message || "Impossible de désactiver les notifications push.",
        );
        return;
      }

      setStatus({
        push_notifications_enabled: false,
        active_subscriptions_count: 0,
        has_active_subscription: false,
      });

      onUserUpdated?.({
        push_notifications_enabled: false,
      });

      toast.success("Notifications push désactivées sur tous vos appareils.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la désactivation globale.");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    try {
      setTesting(true);

      const result = await sendTestPushNotification();

      if (!result?.success) {
        toast.error(result?.message || "Notification de test non envoyée.");
        return;
      }

      toast.success("Notification push de test envoyée !");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du test push.");
    } finally {
      setTesting(false);
    }
  };

  return {
    supported,
    platform,
    standalonePwa,
    isIOSNotStandalone,
    permission,
    loadingStatus,
    saving,
    testing,
    status,
    enablePush,
    disableCurrentDevice,
    disableEverywhere,
    sendTest,
    refreshPushStatus,
    currentDeviceSubscribed,
  };
}
